import { createReducer, WritableDraft } from "@reduxjs/toolkit";
import deepExtend from "deep-extend";
import { Language } from "../../../server/types/index.js";
import {
  FooterModuleSettings,
  LogoCategory,
  Module,
  ModulesById,
  ModuleSettingsTypes,
  ModuleTranslation,
  ModuleTranslations,
  Page,
  PartialModuleSettings,
} from "../../types/index.js";
import {
  getModulesFromAllPages,
  keys,
  ModuleNotFoundError,
  moveArrayElement,
  translateModuleSettings,
} from "../../utils/utils.js";
import { deleteColorScheme } from "../colorSchemes.js";
import { deleteLogoStart, moveLogoStart, upsertLogoStart } from "../logos.js";
import { getPages } from "../pages.js";
import {
  changeModuleStart,
  deleteModuleTranslationStart,
  setModuleSettingsStart,
} from "./actions.js";
import { defaultSettings } from "./constants.js";
import {
  getModules,
  getSiteModules,
  postModule,
  translateModule,
} from "./thunks.js";

export const initialState: ModulesById = {};

const isFooterModule = (
  module: Module,
): module is Module<FooterModuleSettings> => module.type === "FooterModule";

const postModuleStartInternal = (
  state: WritableDraft<ModulesById>,
  action: ReturnType<typeof postModule.pending>,
) => {
  const {
    languageIds,
    moduleType,
    moduleId,
    pageId,
    siteId,
    parentId,
    settings = {},
  } = action.meta.arg;

  const newTranslations = languageIds.reduce<ModuleTranslations>(
    (acc, languageId) => {
      const translation: ModuleTranslation = {
        settings: deepExtend(
          {},
          defaultSettings[moduleType].language,
          settings.language ?? {},
        ),
      };
      return { ...acc, [languageId]: translation };
    },
    {},
  );

  const newModule: Module = {
    colorSchemeId: null,
    id: moduleId,
    pageId,
    siteId,
    type: moduleType,
    parentId,
    translations: newTranslations,
    settings: deepExtend(
      {},
      defaultSettings[moduleType].global,
      settings.global ?? {},
    ),
  };

  state[moduleId] = newModule;
};

const getModulesInternal = (
  state: WritableDraft<ModulesById>,
  modules: Module[],
) => {
  modules.forEach((module) => {
    const { global, language } = defaultSettings[module.type];
    const translations = keys(module.translations).reduce<ModuleTranslations>(
      (acc, languageId) => {
        const translation = module.translations[languageId];
        const settingsbyLanguage = deepExtend(
          {},
          language,
          translation ? translation.settings : {},
        );

        return { ...acc, [languageId]: { settings: settingsbyLanguage } };
      },
      {},
    );

    const currentModule: Module = {
      ...module,
      translations,
      settings: { ...global, ...module.settings },
    };

    state[module.id] = currentModule;
  });
};

const getModulesFromPagesInternal = (
  state: WritableDraft<ModulesById>,
  pages: Page[],
) => getModulesInternal(state, getModulesFromAllPages(pages));

const changeModuleInternal = (
  state: WritableDraft<ModulesById>,
  moduleId: string,
  changeset: Partial<Module>,
) => {
  const currentModule = state[moduleId];
  if (!currentModule) throw new ModuleNotFoundError(moduleId);
  state[moduleId] = { ...currentModule, ...changeset };
};

const translateModuleInternal = (
  state: WritableDraft<ModulesById>,
  moduleId: string,
  languageId: Language,
  sourceLanguageId: Language,
) => {
  const currentModule = state[moduleId];
  const translation = currentModule?.translations[sourceLanguageId];
  const translationAlreadyExists = Boolean(
    currentModule?.translations[languageId],
  );

  if (!translation || translationAlreadyExists) {
    return;
  }

  const newTranslation: ModuleTranslation = {
    settings: translateModuleSettings(translation.settings, languageId),
  };

  changeModuleInternal(state, moduleId, {
    translations: {
      ...currentModule.translations,
      [languageId]: newTranslation,
    },
  });
};

const deleteModuleTranslationInternal = (
  state: WritableDraft<ModulesById>,
  moduleId: string,
  languageId: Language,
  isUntranslated: boolean,
) => {
  const currentModule = state[moduleId];
  if (!currentModule) return;

  // Delete the entire module
  if (isUntranslated) {
    delete state[moduleId];
    return;
  }

  // Delete only one translation translation
  delete currentModule.translations[languageId];
};

const setModuleSettingsInternal = (
  state: WritableDraft<ModulesById>,
  moduleId: string,
  languageId: Language,
  settings: PartialModuleSettings<ModuleSettingsTypes>,
) => {
  const currentModule = state[moduleId];
  if (!currentModule) throw new ModuleNotFoundError(moduleId);
  const translation: ModuleTranslation = currentModule.translations[
    languageId
  ] || { settings: {} };

  currentModule.translations[languageId] = {
    settings: { ...translation.settings, ...(settings.language || {}) },
  };
  currentModule.settings = {
    ...currentModule?.settings,
    ...(settings.global || {}),
  };
};

const deleteModulesColorSchemeInternal = (
  state: WritableDraft<ModulesById>,
  colorSchemeId: string,
) => {
  Object.keys(state).forEach((moduleId) => {
    const currentModule = state[moduleId];
    if (!currentModule) throw new ModuleNotFoundError(moduleId);

    currentModule.colorSchemeId =
      currentModule.colorSchemeId === colorSchemeId
        ? null
        : currentModule.colorSchemeId;
  });
};

const updateLogoCategory = (
  state: WritableDraft<ModulesById>,
  moduleId: string,
  logoCategory: LogoCategory,
  callback: (logoIds: string[]) => string[],
) => {
  const module = state[moduleId];
  if (!module || !isFooterModule(module)) return;

  const { logoCategories } = module.settings;

  changeModuleInternal(state, moduleId, {
    settings: {
      ...module.settings,
      logoCategories: Object.assign([], logoCategories, {
        [logoCategory]: callback([...logoCategories[logoCategory]]),
      }),
    },
  });
};

const moveLogoStartInternal = (
  state: WritableDraft<ModulesById>,
  moduleId: string,
  logoCategory: LogoCategory,
  dragIndex: number,
  hoverIndex: number,
) =>
  updateLogoCategory(state, moduleId, logoCategory, (logoIds) =>
    moveArrayElement(logoIds, dragIndex, hoverIndex),
  );

const deleteLogoStartInternal = (
  state: WritableDraft<ModulesById>,
  moduleId: string,
  logoCategory: LogoCategory,
  logoId: string,
) =>
  updateLogoCategory(state, moduleId, logoCategory, (logoIds) =>
    logoIds.filter((id) => id !== logoId),
  );

const upsertLogoStartInternal = (
  state: WritableDraft<ModulesById>,
  moduleId: string,
  logoCategory: LogoCategory,
  newLogoId: string,
  previousLogoId: string | undefined,
) =>
  updateLogoCategory(state, moduleId, logoCategory, (logoIds) => {
    if (!previousLogoId) {
      return [...logoIds, newLogoId];
    }

    const index = logoIds.indexOf(previousLogoId);

    if (index === -1) {
      return [...logoIds, newLogoId];
    }

    return Object.assign([], logoIds, { [index]: newLogoId });
  });

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(postModule.pending, (state, action) =>
    postModuleStartInternal(state, action),
  );

  builder.addCase(changeModuleStart, (state, action) =>
    changeModuleInternal(
      state,
      action.payload.moduleId,
      action.payload.changeset,
    ),
  );

  builder.addCase(translateModule.pending, (state, action) => {
    const { moduleId, languageId, sourceLanguageId } = action.meta.arg;
    translateModuleInternal(state, moduleId, languageId, sourceLanguageId);
  });

  builder.addCase(deleteModuleTranslationStart, (state, action) => {
    const { moduleId, languageId, deleteAllTranslations } = action.payload;
    deleteModuleTranslationInternal(
      state,
      moduleId,
      languageId,
      deleteAllTranslations,
    );
  });

  builder.addCase(setModuleSettingsStart, (state, action) =>
    setModuleSettingsInternal(
      state,
      action.payload.moduleId,
      action.payload.languageId,
      action.payload.settings,
    ),
  );

  builder.addCase(getPages.fulfilled, (state, action) =>
    getModulesFromPagesInternal(state, action.payload),
  );

  builder.addCase(getSiteModules.fulfilled, (state, action) =>
    getModulesInternal(state, action.payload),
  );

  builder.addCase(getModules.fulfilled, (state, action) =>
    getModulesInternal(state, action.payload),
  );

  builder.addCase(deleteColorScheme.pending, (state, action) =>
    deleteModulesColorSchemeInternal(state, action.meta.arg.colorSchemeId),
  );

  builder.addCase(moveLogoStart, (state, action) =>
    moveLogoStartInternal(
      state,
      action.payload.moduleId,
      action.payload.logoCategory,
      action.payload.dragIndex,
      action.payload.hoverIndex,
    ),
  );

  builder.addCase(deleteLogoStart, (state, action) =>
    deleteLogoStartInternal(
      state,
      action.payload.moduleId,
      action.payload.logoCategory,
      action.payload.logoId,
    ),
  );

  builder.addCase(upsertLogoStart, (state, action) =>
    upsertLogoStartInternal(
      state,
      action.payload.moduleId,
      action.payload.logoCategory,
      action.payload.newLogoId,
      action.payload.previousLogoId,
    ),
  );
});

export default reducer;
